import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/FeelingAdventurous.css';

const CATEGORY_OPTIONS = [
  { value: 'play', label: 'Something to Play', icon: '/static/icons/gamepad-solid.svg' },
  { value: 'watch', label: 'Something to Watch', icon: '/static/icons/film-solid.svg' },
  { value: 'read', label: 'Something to Read', icon: '/static/icons/book-solid.svg' },
  { value: 'listen', label: 'Something to Listen to', icon: '/static/icons/compact-disc-solid.svg' },
];

// Reused on every "I don't have a preference" option so it reads as a real, equally-valid choice.
const DONT_CARE_ICON = '/static/icons/circle-question-solid.svg';

const buildSubcategoryOptions = (category, isAdmin) => {
  switch (category) {
    case 'play':
      return [
        { key: 'steam', value: 'steam', ownershipOnly: false, label: 'A Steam Game', icon: '/static/icons/steam-brands.svg' },
        { key: 'other', value: 'other', ownershipOnly: false, label: 'Another Platform', icon: '/static/icons/puzzle-piece-solid.svg' },
        ...(isAdmin ? [{ key: 'owned', value: 'other', ownershipOnly: true, label: 'Only Owned Games', icon: '/static/icons/square-check-solid.svg' }] : []),
      ];
    case 'watch':
      return [
        { key: 'movie', value: 'movie', ownershipOnly: false, label: 'A Movie', icon: '/static/icons/clapperboard-solid.svg' },
        { key: 'series', value: 'series', ownershipOnly: false, label: 'A Series', icon: '/static/icons/tv-solid.svg' },
      ];
    case 'listen':
      return [
        { key: 'cd', value: 'cd', ownershipOnly: false, label: 'A CD', icon: '/static/icons/compact-disc-solid.svg' },
        { key: 'vinyl', value: 'vinyl', ownershipOnly: false, label: 'Vinyl', icon: '/static/icons/record-vinyl-solid.svg' },
        ...(isAdmin ? [{ key: 'owned', value: 'any', ownershipOnly: true, label: 'Only Owned Music', icon: '/static/icons/square-check-solid.svg' }] : []),
      ];
    case 'read':
      return isAdmin ? [{ key: 'owned', value: 'any', ownershipOnly: true, label: 'Only Owned Books', icon: '/static/icons/square-check-solid.svg' }] : [];
    default:
      return [];
  }
};

const ADVENTURE_ORDER = ['category', 'subcategory', 'finished', 'explicit', 'tags1', 'tags2', 'tags3', 'tags4', 'reveal'];
const SURPRISE_ORDER = ['category', 'tags1', 'tags2', 'reveal'];

// Tier-composition + question size per tag phase, per mode - must stay in sync with the
// `QUESTION_SPECS`/`_adventure_versus_pool`/`_adventure_final_pool` tier logic in
// backend/routes/api/mood_routes.py. 'cloud' = pick 1 from a group of tag chips, 'versus' = pick
// 1 of exactly 2. Surprise Me ("Feeling Lucky") only ever uses the 'lucky' tier composition
// (main pool + 1-2 secondary-pool tags forced in); Feeling Adventurous widens tiers per question.
const TAG_SPECS = {
  surprise: {
    tags1: { question: 'lucky', limit: 6, useRelated: false, kind: 'cloud' },
    tags2: { question: 'lucky', limit: 6, useRelated: true, kind: 'versus' },
  },
  adventure: {
    tags1: { question: 'adventure_1', limit: 7, useRelated: false, kind: 'cloud' },
    tags2: { question: 'adventure_2', limit: 8, useRelated: true, kind: 'cloud' },
    tags3: { question: 'adventure_3', limit: 2, useRelated: true, kind: 'versus' },
    tags4: { question: 'adventure_4', limit: 6, useRelated: true, kind: 'cloud' },
  },
};

const emptyAnswers = () => ({
  category: null,
  subcategory: 'any',
  ownershipOnly: false,
  includeFinished: true,
  explicitMode: 'full',
  tags: [],
});

const MoodWizard = ({ apiBaseUrl, mode, onClose, onAnswer }) => {
  const { isAuthenticated, userRoles } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAuthenticated && userRoles.includes('admin');
  const explicitTier = !isAuthenticated
    ? 'none'
    : userRoles.includes('view-explicit-exclusive')
      ? 'exclusive'
      : userRoles.includes('view-explicit')
        ? 'privileged'
        : 'none';
  const modeKey = mode === 'surprise' ? 'surprise' : 'adventure';

  const [phase, setPhase] = useState('category');
  const [answers, setAnswers] = useState(emptyAnswers);
  const [tagOptions, setTagOptions] = useState([]);
  const [result, setResult] = useState(null);
  const [loadingResult, setLoadingResult] = useState(false);

  const isPhaseApplicable = (phaseName, currentAnswers) => {
    switch (phaseName) {
      case 'subcategory':
        return currentAnswers.category !== 'any' && buildSubcategoryOptions(currentAnswers.category, isAdmin).length > 0;
      case 'finished':
        return isAdmin;
      case 'explicit':
        return explicitTier === 'privileged';
      default:
        return true;
    }
  };

  const computeNextPhase = (from, currentAnswers) => {
    const order = mode === 'surprise' ? SURPRISE_ORDER : ADVENTURE_ORDER;
    let idx = order.indexOf(from) + 1;
    while (idx < order.length - 1 && !isPhaseApplicable(order[idx], currentAnswers)) {
      idx += 1;
    }
    return order[idx];
  };

  const fetchTags = async (currentAnswers, extra = {}) => {
    const params = new URLSearchParams({
      category: currentAnswers.category || 'any',
      subcategory: currentAnswers.subcategory || 'any',
      ownership_only: currentAnswers.ownershipOnly ? 'true' : 'false',
      include_finished: currentAnswers.includeFinished === false ? 'false' : 'true',
      explicit_mode: currentAnswers.explicitMode || 'full',
      limit: String(extra.limit || 6),
    });
    if (extra.question) params.set('question', extra.question);
    if (extra.exclude?.length) params.set('exclude', extra.exclude.join(','));
    if (extra.relatedTo?.length) params.set('related_to', extra.relatedTo.join(','));
    try {
      const res = await fetch(`${apiBaseUrl}/mood/tags?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) return [];
      return await res.json();
    } catch (err) {
      console.error('Failed to fetch mood tags:', err);
      return [];
    }
  };

  const finish = async (currentAnswers) => {
    setPhase('reveal');
    setLoadingResult(true);
    try {
      const res = await fetch(`${apiBaseUrl}/mood/pick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          category: currentAnswers.category,
          subcategory: currentAnswers.subcategory,
          ownership_only: currentAnswers.ownershipOnly,
          include_finished: currentAnswers.includeFinished,
          explicit_mode: currentAnswers.explicitMode,
          tags: currentAnswers.tags,
        }),
      });
      const data = await res.json();
      setResult(data.media || null);
    } catch (err) {
      console.error('Failed to get a mood-based pick:', err);
      setResult(null);
    } finally {
      setLoadingResult(false);
    }
  };

  const goPhase = async (from, currentAnswers) => {
    setAnswers(currentAnswers);

    const next = computeNextPhase(from, currentAnswers);
    const tagSpec = TAG_SPECS[modeKey][next];
    if (tagSpec) {
      setPhase('loading');
      const options = await fetchTags(currentAnswers, {
        question: tagSpec.question,
        limit: tagSpec.limit,
        exclude: currentAnswers.tags,
        relatedTo: tagSpec.useRelated ? currentAnswers.tags : [],
      });
      if (options.length === 0) {
        // Nothing left for this tag phase (e.g. too few tags left after narrowing) - skip
        // straight to whatever comes after it instead of dead-ending the wizard.
        return goPhase(next, currentAnswers);
      }
      setTagOptions(options);
      setPhase(next);
      return;
    }
    if (next === 'reveal') {
      return finish(currentAnswers);
    }
    setPhase(next);
  };

  const chooseCategory = (category) => {
    onAnswer?.();
    goPhase('category', { ...answers, category, subcategory: 'any', ownershipOnly: false });
  };

  const chooseSubcategory = (opt) => {
    onAnswer?.();
    goPhase('subcategory', { ...answers, subcategory: opt.value, ownershipOnly: opt.ownershipOnly });
  };

  const chooseFinished = (includeFinished) => {
    onAnswer?.();
    goPhase('finished', { ...answers, includeFinished });
  };

  const chooseExplicit = (explicitMode) => {
    onAnswer?.();
    goPhase('explicit', { ...answers, explicitMode });
  };

  const selectTag = (fromPhase, tag) => {
    onAnswer?.();
    goPhase(fromPhase, { ...answers, tags: [...answers.tags, tag] });
  };

  const dontMatterTag = async (fromPhase) => {
    onAnswer?.();
    setPhase('loading');
    const pool = await fetchTags(answers, { exclude: answers.tags, limit: 50 });
    const nextAnswers = { ...answers };
    if (pool.length > 0) {
      nextAnswers.tags = [...answers.tags, pool[Math.floor(Math.random() * pool.length)]];
    }
    goPhase(fromPhase, nextAnswers);
  };

  // The result is deliberately never shown on the card itself - the whole point of the reveal
  // button is that the media page is the first place you actually see what you got.
  const goToMedia = () => {
    if (result) navigate(`/media-item/${result.id}`);
  };

  const phaseTitles = {
    category: 'What are you in the mood for?',
    subcategory: 'Narrow it down a little.',
    finished: 'Include stuff you already finished?',
    explicit: 'How spicy should this get?',
    tags1: "What's calling to you right now?",
    tags2: modeKey === 'adventure' ? 'Pick another feeling to explore.' : 'One more feeling check.',
    tags3: 'Which do you prefer?',
    tags4: "Let's narrow it down one last time.",
  };

  const currentTagKind = TAG_SPECS[modeKey][phase]?.kind;

  return (
    <div className="mood-inline">
      <button type="button" className="mood-inline-back" onClick={onClose}>
        <img src="/static/icons/arrow-left-solid.svg" className="icon" alt="" /> Start Over
      </button>

      {phase === 'loading' && (
          <div className="mood-phase mood-loading">
            <img src="/static/icons/circle-notch-solid.svg" className="icon mood-spin" alt="" />
            <p>Thinking…</p>
          </div>
        )}

        {phase === 'category' && (
          <div className="mood-phase">
            <h3>{phaseTitles.category}</h3>
            <div className="mood-options-grid">
              {CATEGORY_OPTIONS.map((opt) => (
                <button key={opt.value} type="button" className="mood-option-card" onClick={() => chooseCategory(opt.value)}>
                  <img src={opt.icon} className="icon" alt="" />
                  {opt.label}
                </button>
              ))}
              <button type="button" className="mood-option-card mood-option-card-neutral" onClick={() => chooseCategory('any')}>
                <img src={DONT_CARE_ICON} className="icon" alt="" />
                Doesn't Matter
              </button>
            </div>
          </div>
        )}

        {phase === 'subcategory' && (
          <div className="mood-phase">
            <h3>{phaseTitles.subcategory}</h3>
            <div className="mood-options-grid">
              {buildSubcategoryOptions(answers.category, isAdmin).map((opt) => (
                <button key={opt.key} type="button" className="mood-option-card" onClick={() => chooseSubcategory(opt)}>
                  <img src={opt.icon} className="icon" alt="" />
                  {opt.label}
                </button>
              ))}
              <button type="button" className="mood-option-card mood-option-card-neutral" onClick={() => chooseSubcategory({ value: 'any', ownershipOnly: false })}>
                <img src={DONT_CARE_ICON} className="icon" alt="" />
                Doesn't Matter
              </button>
            </div>
          </div>
        )}

        {phase === 'finished' && (
          <div className="mood-phase">
            <h3>{phaseTitles.finished}</h3>
            <div className="mood-options-grid">
              <button type="button" className="mood-option-card" onClick={() => chooseFinished(true)}>
                <img src="/static/icons/check-double-solid.svg" className="icon" alt="" />
                Sure, Anything Goes
              </button>
              <button type="button" className="mood-option-card" onClick={() => chooseFinished(false)}>
                <img src="/static/icons/filter-solid.svg" className="icon" alt="" />
                No, Only Unfinished
              </button>
            </div>
          </div>
        )}

        {phase === 'explicit' && (
          <div className="mood-phase">
            <h3>{phaseTitles.explicit}</h3>
            <div className="mood-options-grid">
              <button type="button" className="mood-option-card" onClick={() => chooseExplicit('family')}>
                <img src="/static/icons/shield-heart-solid.svg" className="icon" alt="" />
                Family Friendly
              </button>
              <button type="button" className="mood-option-card mood-option-card-neutral" onClick={() => chooseExplicit('full')}>
                <img src={DONT_CARE_ICON} className="icon" alt="" />
                Doesn't Matter
              </button>
              <button type="button" className="mood-option-card" onClick={() => chooseExplicit('explicit')}>
                <img src="/static/icons/unlock-solid.svg" className="icon" alt="" />
                Only Explicit
              </button>
            </div>
          </div>
        )}

        {currentTagKind === 'cloud' && (
          <div className="mood-phase">
            <h3>{phaseTitles[phase]}</h3>
            <div className="mood-tag-cloud">
              {tagOptions.map((tag) => (
                <button key={tag} type="button" className="mood-tag-chip" onClick={() => selectTag(phase, tag)}>
                  {tag}
                </button>
              ))}
            </div>
            <button type="button" className="mood-tag-chip mood-dont-mind-btn" onClick={() => dontMatterTag(phase)}>
              <img src={DONT_CARE_ICON} className="icon" alt="" />
              Doesn't Matter
            </button>
          </div>
        )}

        {currentTagKind === 'versus' && (
          <div className="mood-phase">
            <h3>{phaseTitles[phase]}</h3>
            <div className="mood-versus">
              {tagOptions.slice(0, 2).map((tag, i) => (
                <React.Fragment key={tag}>
                  {i === 1 && <span className="mood-versus-divider">or</span>}
                  <button type="button" className="mood-versus-card" onClick={() => selectTag(phase, tag)}>
                    Something that feels like<br /><strong>{tag}</strong>
                  </button>
                </React.Fragment>
              ))}
            </div>
            <button type="button" className="mood-tag-chip mood-dont-mind-btn" onClick={() => dontMatterTag(phase)}>
              <img src={DONT_CARE_ICON} className="icon" alt="" />
              Doesn't Matter
            </button>
          </div>
        )}

        {phase === 'reveal' && (
          <div className="mood-phase mood-reveal">
            {loadingResult && (
              <>
                <img src="/static/icons/circle-notch-solid.svg" className="icon mood-spin" alt="" />
                <p>Rolling the dice on your next adventure…</p>
              </>
            )}
            {!loadingResult && result && (
              <>
                <h3>Your pick is ready.</h3>
                <p>No peeking here, reveal it on its own page.</p>
                <button type="button" className="mood-reveal-btn" onClick={goToMedia}>
                  <img src="/static/icons/ticket-simple-solid.svg" className="icon" alt="" />
                  Reveal My Pick
                </button>
              </>
            )}
            {!loadingResult && !result && (
              <>
                <h3>Hmm, nothing matched.</h3>
                <p>Try loosening things up a little.</p>
                <button type="button" className="btn btn-secondary" onClick={onClose}>Start Over</button>
              </>
            )}
          </div>
        )}
    </div>
  );
};

export default MoodWizard;
